import React, { useState, useEffect, useMemo, useRef } from 'react';
import { connect, useSelector } from 'react-redux';
import { useVirtualizer } from '@tanstack/react-virtual';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Loader } from '../../../Common';
import moment from 'moment';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useGetExpenseCategoriesQuery } from '../../../../api/expenseApi';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { selectedUserIdSelector } from '../../../../selectors/userSelectors';
import { useGetUserInfoQuery, useGetExpensesQuery, useSaveExpenseChangeMutation } from '../../../../api/userApi';
import { isEmpty } from 'lodash';
import { faHourglassHalf, faChevronDown } from '@fortawesome/free-solid-svg-icons';

const STATUS_OPTIONS = ['prob', 'maybe', 'yes', 'no', 'maybe*', '-'];

/** Background colors for each status option */
const STATUS_COLORS = {
  prob: '#fac65b',
  maybe: '#ff9554',
  yes: '#52cf98',
  no: '#fb505a',
  'maybe*': '#9ea2a8',
  '-': 'white'
};

const formatDisplayDate = (date) => {
  const today = moment().format('MM/DD/YYYY');
  const yesterday = moment().subtract(1, 'days').format('MM/DD/YYYY');
  const formattedDate = moment(date).format('MM/DD/YYYY');

  if (formattedDate === today) {
    return 'Today';
  } else if (formattedDate === yesterday) {
    return 'Yesterday';
  }
  return formattedDate;
};

const ExpenseHeader = ({ expenseList, index }) => {
  const currentDate = expenseList[index].date;
  const prevDate = index > 0 ? expenseList[index - 1].date : null;

  // Only show header if it's the first item or date has changed
  if (index === 0 || moment(currentDate).format('MM/DD/YYYY') !== moment(prevDate).format('MM/DD/YYYY')) {
    return (
      <Typography
        sx={[
          {
            p: 1,
            textAlign: 'center'
          },
          (theme) => ({
            background: theme.palette.gainsboro.main
          })
        ]}
      >
        {formatDisplayDate(currentDate)}
      </Typography>
    );
  }
  return null;
};

const ExpenseSection = () => {
  const selectedUserId = useSelector(selectedUserIdSelector);

  const [saveResponseMessage, setSaveResponseMessage] = useState('');
  const [isCopySnackbarOpen, setIsCopySnackbarOpen] = useState(false);

  const currentYear = moment().year();
  const YEAR_OPTIONS = [currentYear, currentYear - 1];
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const [saveExpenseChange, { data: saveResponse, isError }] = useSaveExpenseChangeMutation();

  const { data: expenseCategories, isLoading: areExpenseCategoriesLoading } = useGetExpenseCategoriesQuery();
  const { data: expenses = [], isLoading: areExpensesLoading } = useGetExpensesQuery({
    userId: selectedUserId,
    year: selectedYear
  });
  const { data: userInfo, isLoading: isUserInfoLoading } = useGetUserInfoQuery(selectedUserId);

  const [expenseList, setExpenseList] = useState(expenses.map((expense) => ({ ...expense })));

  const scrollRef = useRef(null);
  const virtualizer = useVirtualizer({
    count: expenseList.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 64,
    overscan: 8
  });

  // Switching year tabs reuses the same scroll container, so reset it back to the
  // top rather than leaving the user wherever they were scrolled in the prior year
  useEffect(() => {
    if (scrollRef.current) {
      virtualizer.scrollToOffset(0);
    }
  }, [selectedYear, virtualizer]);

  useEffect(() => {
    if (!areExpensesLoading && expenses) {
      // Create mutable copies of each expense object
      // TODO: Refactor this Soon™. Having to create a mutable copy of each expense object like this is kinda gross.
      const mutableExpenses = expenses.map((expense) => ({ ...expense }));
      setExpenseList(mutableExpenses);
    }
  }, [expenses, areExpensesLoading]);

  const expenseCategoryOptions = useMemo(() => {
    if (!areExpenseCategoriesLoading && expenseCategories) {
      return expenseCategories.map((category) => ({
        ...category,
        display_name: category.id === expenseCategories[0].id ? 'none' : category.display_name
      }));
    }
    return expenseCategories;
  }, [expenseCategories, areExpenseCategoriesLoading]);

  useEffect(() => {
    if (isError) {
      setSaveResponseMessage('Something went wrong, please try again');
    } else if (saveResponse) {
      setSaveResponseMessage('Expense edited successfully!');
    }
  }, [isError, saveResponse]);

  const handleTextChange = (e, index) => {
    const newExpenseList = [...expenseList];
    newExpenseList[index].clean_name = e.target.value;
    newExpenseList[index].changed = true;
    setExpenseList(newExpenseList);
  };

  const handleTextBlur = (index) => {
    if (expenseList[index].changed) {
      handleSaveChanges(expenseList, index);
    }
  };

  const handleStatusChange = (e, index) => {
    const newExpenseList = [...expenseList];
    newExpenseList[index].status = e.target.value;
    newExpenseList[index].changed = true;
    setExpenseList(newExpenseList);
    handleSaveChanges(newExpenseList, index);
  };

  const handleCategoryChange = (e, index) => {
    const value = e.target.value;
    const newExpenseList = [...expenseList];
    newExpenseList[index].keeper_category_id = value;
    newExpenseList[index].changed = true;
    const keeperCategory = expenseCategoryOptions.find((option) => option.id === value);
    newExpenseList[index].keeper_category = keeperCategory.display_name;
    setExpenseList(newExpenseList);
    handleSaveChanges(newExpenseList, index);
  };

  const handleAmountChange = (e, index) => {
    const val = e.target.value;
    const newExpenseList = [...expenseList];
    newExpenseList[index].amount = val;
    newExpenseList[index].changed = true;
    setExpenseList(newExpenseList);
  };

  const handleAmountBlur = (e, index) => {
    const val = parseFloat(e.target.value);
    const newExpenseList = [...expenseList];
    newExpenseList[index].amount = val % 1 !== 0 ? val.toFixed(2) : val;
    newExpenseList[index].changed = true;
    setExpenseList(newExpenseList);
    handleSaveChanges(newExpenseList, index);
  };

  /**
   * @desc save changes made in expense list
   */
  const handleSaveChanges = async (expenseList, index) => {
    const editedExpense = expenseList[index];
    const obj = {
      userId: selectedUserId,
      transactionId: editedExpense.transaction_id,
      editedExpense: {
        status: editedExpense.status,
        clean_name: editedExpense.clean_name,
        keeper_category: editedExpense.keeper_category,
        keeper_category_id: editedExpense.keeper_category_id,
        amount: editedExpense.amount
      }
    };
    await saveExpenseChange(obj);
  };

  const isLoading = areExpenseCategoriesLoading || isUserInfoLoading || areExpensesLoading;

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <Tabs
        value={selectedYear}
        onChange={(_e, year) => setSelectedYear(year)}
        variant='fullWidth'
        textColor='secondary'
        indicatorColor='secondary'
      >
        {YEAR_OPTIONS.map((year) => (
          <Tab key={year} value={year} label={year} />
        ))}
      </Tabs>
      {isLoading ? (
        <Stack
          sx={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(87, 185, 255, 0.2)'
          }}
        >
          <Loader />
        </Stack>
      ) : isEmpty(expenseList) ? (
        <Stack
          sx={[
            {
              flex: 1,
              p: 2,
              textAlign: 'center',
              justifyContent: 'center'
            },
            (theme) => ({
              background: 'white',
              boxShadow: `0 0 3px ${theme.palette.gainsboro.main}`
            })
          ]}
        >
          <Typography data-testid='expense-section' variant='h5'>
            Expenses are not available
          </Typography>
        </Stack>
      ) : (
        <Box
          data-testid='expense-section'
          ref={scrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            background: 'white'
          }}
        >
          <div
            style={{
              height: virtualizer.getTotalSize(),
              width: '100%',
              position: 'relative'
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const index = virtualRow.index;
              const expense = expenseList[index];
              return (
                <div
                  key={expense.transaction_id}
                  data-index={index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                >
                  <ExpenseHeader expenseList={expenseList} index={index} />
                  <Accordion>
                    <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
                      <Stack
                        direction='row'
                        sx={{
                          height: 'auto',
                          width: '100%',
                          gap: 2
                        }}
                      >
                        <Select
                          value={expense.status}
                          onChange={(e) => handleStatusChange(e, index)}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          sx={{ backgroundColor: STATUS_COLORS[expense.status], minWidth: '110px' }}
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option} sx={{ backgroundColor: STATUS_COLORS[option] }}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        <TextField
                          variant='outlined'
                          onChange={(e) => handleTextChange(e, index)}
                          onBlur={() => handleTextBlur(index)}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          value={expense.clean_name}
                          sx={{ width: '45%' }}
                        />
                        <Select
                          value={expense.keeper_category_id}
                          onChange={(e) => handleCategoryChange(e, index)}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          sx={{
                            width: '20%'
                          }}
                        >
                          {expenseCategoryOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.display_name}
                            </MenuItem>
                          ))}
                        </Select>
                        <TextField
                          variant='outlined'
                          sx={{ width: '15%' }}
                          value={expense.amount}
                          onChange={(e) => handleAmountChange(e, index)}
                          onBlur={(e) => handleAmountBlur(e, index)}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position='start'>$</InputAdornment>
                            }
                          }}
                        />
                        {expense.pending === 0 && (
                          <Stack
                            direction='row'
                            sx={{
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 1
                            }}
                          >
                            <Tooltip title='Pending' placement='top'>
                              <FontAwesomeIcon icon={faHourglassHalf} />
                            </Tooltip>
                          </Stack>
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          width: '100%',
                          padding: '10px'
                        }}
                      >
                        <Stack
                          direction='row'
                          sx={{
                            alignItems: 'center',
                            width: '100%',
                            gap: 1
                          }}
                        >
                          <Typography
                            variant='body1'
                            sx={{
                              fontWeight: 'bold',
                              minWidth: '200px',
                              textAlign: 'left'
                            }}
                          >
                            Raw Description:{' '}
                          </Typography>
                          <Typography variant='body1'>{expense.name ? expense.name : 'N/A'}</Typography>
                          <CopyToClipboard
                            text={expense.name ? expense.name : 'N/A'}
                            onCopy={() => setIsCopySnackbarOpen(true)}
                          >
                            <IconButton size='small'>
                              <FontAwesomeIcon icon={['fa', 'copy']} />
                            </IconButton>
                          </CopyToClipboard>
                        </Stack>
                        <Stack
                          direction='row'
                          sx={{
                            alignItems: 'center',
                            width: '100%',
                            gap: 1
                          }}
                        >
                          <Typography
                            variant='body1'
                            sx={{
                              fontWeight: 'bold',
                              minWidth: '200px',
                              textAlign: 'left'
                            }}
                          >
                            Plaid Category:{' '}
                          </Typography>
                          <Typography variant='body1'>{expense.category}</Typography>
                        </Stack>
                        <Stack
                          direction='row'
                          sx={{
                            alignItems: 'center',
                            width: '100%',
                            gap: 1
                          }}
                        >
                          <Typography
                            variant='body1'
                            sx={{
                              fontWeight: 'bold',
                              minWidth: '200px',
                              textAlign: 'left'
                            }}
                          >
                            Location:{' '}
                          </Typography>
                          <Typography variant='body1'>{expense.location}</Typography>
                        </Stack>
                        <Stack
                          direction='row'
                          sx={{
                            alignItems: 'center',
                            width: '100%',
                            gap: 1
                          }}
                        >
                          <Typography
                            variant='body1'
                            sx={{
                              fontWeight: 'bold',
                              minWidth: '200px',
                              textAlign: 'left'
                            }}
                          >
                            Account / Bank Name:{' '}
                          </Typography>
                          <Typography variant='body1'>
                            {expense.bank_acct_name} {expense.mask ? `- ${expense.mask}` : ''}
                          </Typography>
                        </Stack>
                        <Stack
                          direction='row'
                          sx={{
                            alignItems: 'center',
                            width: '100%',
                            gap: 1
                          }}
                        >
                          <Typography
                            variant='body1'
                            sx={{
                              fontWeight: 'bold',
                              minWidth: '200px',
                              textAlign: 'left'
                            }}
                          >
                            $ Saved:{' '}
                          </Typography>
                          <Typography variant='body1'>
                            {expense.tax_profile_logic === 'home'
                              ? ((((userInfo.percent_home / 100) * userInfo.tax_rate) / 100) * expense.amount).toFixed(
                                  2
                                )
                              : expense.tax_profile_logic === 'car'
                                ? ((((userInfo.percent_car / 100) * userInfo.tax_rate) / 100) * expense.amount).toFixed(
                                    2
                                  )
                                : expense.tax_profile_logic === 'phone'
                                  ? (
                                      (((userInfo.percent_phone / 100) * userInfo.tax_rate) / 100) *
                                      expense.amount
                                    ).toFixed(2)
                                  : expense.tax_profile_logic === 'meals'
                                    ? (((0.5 * userInfo.tax_rate) / 100) * expense.amount).toFixed(2)
                                    : ((userInfo.tax_rate / 100) * expense.amount).toFixed(2)}
                          </Typography>
                        </Stack>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </div>
              );
            })}
          </div>
        </Box>
      )}
      <Snackbar
        open={!isEmpty(saveResponseMessage)}
        autoHideDuration={6000}
        onClose={() => setSaveResponseMessage('')}
        message={saveResponseMessage}
      />
      <Snackbar
        open={isCopySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setIsCopySnackbarOpen(false)}
        message='Copied to clipboard!'
      />
    </Stack>
  );
};

const ConnectedExpenseSection = connect(null, {})(ExpenseSection);

export default ConnectedExpenseSection;
